import { useQuery } from "@tanstack/react-query";
import { reportApi } from "./axios";
import {
  DataI,
  ServiceResponseI,
  PropsReportI,
  ReportI,
  ObjPostI,
} from "../../interfaces";
import { ValidateCurrentToken, LogoutSystem } from "../../helpers";
import { AxiosError } from "axios";

export const getReports = async ({
  pagination,
  sorting,
  globalFilter,
  client,
  supplier,
  equipment,
  ot,
  startDate,
  endDate,
}: PropsReportI): Promise<ServiceResponseI<DataI<ReportI>>> => {
  try {
    const validateToken = ValidateCurrentToken();

    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();

    params.append("page", (pagination!.pageIndex + 1).toString());
    params.append("limit", pagination!.pageSize.toString());
    params.append("search", globalFilter ?? "");
    params.append("sorting", JSON.stringify(sorting ?? []));
    client && params.append("client", client.toString());
    supplier && params.append("supplier", supplier.toString());
    equipment && params.append("equipment", equipment.toString());
    ot && params.append("report", ot.toString());

    startDate && params.append("startDate", startDate);
    endDate && params.append("endDate", endDate);

    //headers: { 'Authorization': validateToken
    const { data } = await reportApi.get<ServiceResponseI<DataI<ReportI>>>("", {
      params,
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;

    if (obj?.status === 401) {
      LogoutSystem();
    }

    throw new Error((err as Error).message);
  }
};

export const getAllReports = async (
  client?: number | null
): Promise<ServiceResponseI<ReportI[]>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();
    client && params.append("client", client.toString());
    const { data } = await reportApi.get<ServiceResponseI<ReportI[]>>(
      `/all-no-pagination`,
      { headers: { Authorization: validateToken }, params }
    );

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      LogoutSystem();
    }
    throw new Error((err as Error).message);
  }
};

export const reportPost = async <T>(
  getForms: T
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await reportApi.post<ServiceResponseI<string>>(
      "",
      getForms
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const reportPostForSupplier = async <T>(
  getForms: T
): Promise<ServiceResponseI<string>> => {
  try {
    // const validateToken = ValidateCurrentToken();
    // if (!validateToken) throw new Error('TOKEN INVALIDO');
    const { data } = await reportApi.post<ServiceResponseI<string>>(
      "/for-supplier",
      getForms
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const reportPut = async <T>(
  id: number,
  updateForms: T
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await reportApi.put<ServiceResponseI<string>>(
      `/${id}`,
      updateForms,
      { headers: { Authorization: validateToken } }
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const reportById = async (
  id: number
): Promise<ServiceResponseI<ReportI>> => {
  try {
    // const validateToken = ValidateCurrentToken();
    // if (!validateToken) throw new Error('TOKEN INVALIDO');
    const { data } = await reportApi.get<ServiceResponseI<ReportI>>(`/${id}`);
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const reportDelete = async (
  id: number
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await reportApi.delete<ServiceResponseI<string>>(
      `/${id}`,
      { headers: { Authorization: validateToken } }
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const reportPdfbyId = async (
  id: number
): Promise<ServiceResponseI<any>> => {
  try {
    // const validateToken = ValidateCurrentToken();
    // if (!validateToken) throw new Error('TOKEN INVALIDO');
    const { data } = await reportApi.get<ServiceResponseI<any>>(
      `/generate-pdf/${id}`
    );

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    // if (obj?.status === 401) {

    //     LogoutSystem();
    // }
    throw new Error((obj?.data as Error).message);
  }
};

export const useReports = ({
  sorting,
  pagination,
  globalFilter,
  client,
  supplier,
  equipment,
  ot,
  startDate,
  endDate,
}: PropsReportI) => {
  const reportsQuery = useQuery(
    [
      "reports",
      {
        pagination,
        sorting,
        globalFilter,
        client,
        supplier,
        equipment,
        ot,
        startDate,
        endDate,
      },
    ],
    () =>
      getReports({
        pagination,
        sorting,
        globalFilter,
        client,
        supplier,
        equipment,
        ot,
        startDate,
        endDate,
      }),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      cacheTime: 0,
      staleTime: 0,
    }
  );

  return {
    reportsQuery,
  };
};
